import { ExclamationCircleFilled } from '@ant-design/icons'
import { typeAppearance, updateTypeIcons, updateTypes } from '@constants/DSUReport'
import { BBDTag, GraviButton, GraviGrid, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Alert, Col, Popover, Row, Space } from 'antd'
import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'
import { IconBug } from '@tabler/icons-react'

const STORAGE_KEY = 'dsu-report'

export function Grid({ setSelectedUpdate, DSUOverviewData, status }) {
  const { updateSetting } = useUserSettings()
  const getRowId = useCallback((row) => row.data?.id, [])
  const columnDefs = useMemo(() => getColumnDefs(), [])
  const agPropOverrides = useMemo(() => ({ getRowId, columnDefs }), [getRowId, columnDefs])

  const debug_info = DSUOverviewData?.debug_info || {}

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Device Status Updates',
        actionButtons: (
          <Popover
            placement='bottomRight'
            content={
              <Vertical>
                <Texto weight='bold' appearance='secondary'>
                  Schedule ID: {debug_info.schedule_id}
                </Texto>
                <Texto>Expected Schedule Version: {debug_info.current_schedule_version}</Texto>
                <Texto>Last Sent Scheduled Version: {debug_info.last_seen_schedule_version}</Texto>
                <Texto>App Version: {debug_info.last_seen_app_version}</Texto>
              </Vertical>
            }
          >
            <GraviButton icon={<IconBug size={20} />} type='text' className='ml-2' />
          </Popover>
        ),
      }}
      loading={status === 'pending'}
      agPropOverrides={agPropOverrides}
      rowGroupPanelShow='never'
      storageKey={STORAGE_KEY}
      rowData={DSUOverviewData?.rows}
      rowSelection='single'
      onSelectionChanged={(props) => {
        setSelectedUpdate(props.api.getSelectedRows()[0])
      }}
      onFirstDataRendered={(params) => {
        params.api.forEachNodeAfterFilterAndSort((node, index) => node.setSelected(index === 0))
      }}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = () => [
  {
    field: 'has_error',
    headerName: '',
    maxWidth: 100,
    cellRenderer: (value, row) => {
      return (
        row.has_error === true && (
          <Popover title='Error Message' content={<ErrorPopoverContent errorMsg={row.error_msg} />}>
            <ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />
          </Popover>
        )
      )
    },
  },
  {
    headerName: 'Type',
    field: 'type',
    cellRenderer: ({ value }) => <CustomTypeColumn type={value} />,
  },
  {
    headerName: 'Requested',
    field: 'date',
    filter: 'agDateColumnFilter',
    cellRenderer: ({ value }) => <Texto weight='bold'> {formatters.DATE_TIME(moment(value))}</Texto>,
  },
  {
    headerName: 'Time Until Received',
    field: 'date_received',
    cellRenderer: ({ value, data }) => {
      const minSinceReceived = moment.duration(moment(value).diff(data.date)).asMinutes().toFixed(2)
      const secSinceReceived = moment.duration(moment(value).diff(data.date)).asSeconds().toFixed(2)
      return (
        <Texto>
          {secSinceReceived
            ? minSinceReceived < 1
              ? `${secSinceReceived} Seconds`
              : `${minSinceReceived} Minutes`
            : 'Not Received'}{' '}
        </Texto>
      )
    },
  },
  {
    headerName: 'Time Until Processed',
    field: 'date_processed',
    cellRenderer: ({ value, data }) => {
      return (
        <Texto>
          {value
            ? `${moment.duration(moment(value).diff(data.date_received)).asSeconds().toFixed(2)} Seconds`
            : 'Not Processed'}
        </Texto>
      )
    },
  },
  {
    headerName: 'Offline',
    field: 'offline',
    cellRenderer: ({ value }) => {
      if (value === true) {
        return <BBDTag warning>OFFLINE</BBDTag>
      }
      return null
    },
  },
]

function CustomTypeColumn({ type }) {
  return (
    <Row align='middle'>
      <Space>
        <Col>
          <Texto appearance={typeAppearance[type]}>{updateTypeIcons[type]}</Texto>
        </Col>
        <Col>
          <Texto weight='bold' appearance={typeAppearance[type]}>
            {updateTypes[type]}
          </Texto>
        </Col>
      </Space>
    </Row>
  )
}
function ErrorPopoverContent({ errorMsg }) {
  return (
    <Row>
      <Col span={24}>
        <Alert message={errorMsg || 'No Message'} type='error' />
      </Col>
    </Row>
  )
}
